import { useEffect, useState } from "react";
import api from "@/lib/api";
import { CheckCircle2, History, Plus, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { dec, eur, pill } from "@/components/excelVoce/format";
import { MisuraDialog } from "./MisuraDialog";
import { StoricoMisura } from "./StoricoMisura";
import { FunzioniSpente, canEditSal, run, useFunzioniExcel } from "./labels";

const Box = ({ label, v, testid, cls = "" }) => (
  <div className="rounded-md border p-2" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
    <div className={`font-mono text-sm mt-0.5 ${cls}`}>{v}</div>
  </div>
);

const Riconciliazione = ({ r, um }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2" data-testid="misure-riconciliazione">
    <Box label="Computo" v={`${dec(r.quantita_computo)} ${um || ""}`} testid="ric-computo" />
    <Box label="Eseguita (misure)" v={dec(r.eseguita)} testid="ric-eseguita" />
    <Box label="Verificata" v={dec(r.verificata)} testid="ric-verificata" cls="text-emerald-700" />
    <Box label="Da verificare" v={dec(r.da_verificare)} testid="ric-da-verificare" cls="text-amber-700" />
    <Box label="Contab. SAL generale" v={dec(r.contabilizzata_generale)} testid="ric-cont-generale" cls="text-sky-800 font-semibold" />
    <Box label="Contab. SAL subappaltatori" v={dec(r.contabilizzata_subappaltatori)} testid="ric-cont-sub" />
    <Box label="Quantità registro E4" v={dec(r.e4_quantita_manodopera)} testid="ric-e4" />
    <Box label="Costo effettivo E4" v={`${eur(r.costo_effettivo_determinato)}${r.costi_da_determinare ? ` (+${r.costi_da_determinare} da det.)` : ""}`} testid="ric-costo" />
  </div>
);

const Stato = ({ m }) => (
  <div className="flex flex-wrap gap-1">
    {m.contabilizzata.map((c) => <span key={c.sal_id} className={`${pill} bg-sky-50 text-sky-800 border-sky-200`}>{c.rapporto === "GENERALE" ? "SAL generale" : "SAL sub."} · {c.sal}</span>)}
    {m.origine_ripartizione_id && <span className={`${pill} text-slate-500`}>da Registro E4</span>}
  </div>
);

export const LibrettoMisure = ({ rigaId, um }) => {
  const { user } = useAuth();
  const fx = useFunzioniExcel();
  const ed = canEditSal(user);
  const [d, setD] = useState(null);
  const [dlg, setDlg] = useState(null);
  const [storico, setStorico] = useState(null);
  const load = () => api.get(`/righe/${rigaId}/misure`).then((r) => setD(r.data));
  useEffect(() => { load(); }, [rigaId]);
  const verifica = async (m) => { if (await run(() => api.post(`/misure/${m.id}/verifica`, { verificata: !m.verificata }), m.verificata ? "Verifica rimossa" : "Misura verificata")) load(); };
  const elimina = async (m) => { if (await run(() => api.delete(`/misure/${m.id}`), "Misura eliminata")) load(); };
  if (!d) return <p className="text-sm text-slate-500 p-4">Caricamento…</p>;
  return (
    <Card data-testid="libretto-misure"><CardContent className="p-4 sm:p-6 space-y-4">
      <div className="flex flex-wrap items-start gap-2">
        <div className="mr-auto max-w-2xl">
          <div className="text-xs uppercase tracking-widest text-sky-700 font-semibold">Libretto delle misure</div>
          <p className="text-xs text-slate-500 mt-0.5">Quantità eseguite e misurate. Solo le misure verificate dal tecnico entrano nei SAL: tutte nel SAL generale, quelle di un subappaltatore anche nel suo SAL. I costi restano nel Registro effettivo.</p>
        </div>
        {ed && <Button size="sm" onClick={() => setDlg({})} data-testid="misura-nuova"><Plus className="h-3.5 w-3.5 mr-1" /> Misura</Button>}
      </div>
      <FunzioniSpente st={fx} />
      <Riconciliazione r={d.riconciliazione} um={um} />
      <div className="overflow-x-auto">
        <Table data-testid="misure-lista">
          <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Esecutore</TableHead><TableHead className="text-right">Quantità</TableHead><TableHead>Note</TableHead><TableHead>Verifica</TableHead><TableHead>SAL</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {d.misure.map((m) => {
              const bloc = m.contabilizzata.length > 0;
              return (
                <TableRow key={m.id} data-testid={`misura-${m.id}`}>
                  <TableCell className="text-xs">{m.data}</TableCell>
                  <TableCell className="text-xs">{m.esecutore}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{dec(m.quantita)} {um}</TableCell>
                  <TableCell className="text-xs max-w-xs truncate" title={m.note || ""}>{m.note}</TableCell>
                  <TableCell>
                    <button type="button" disabled={!ed || bloc} onClick={() => verifica(m)} data-testid={`misura-verifica-${m.id}`}
                      className={`${pill} ${m.verificata ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-amber-50 text-amber-800 border-amber-200"}`}>
                      <CheckCircle2 className="h-3 w-3 mr-1" />{m.verificata ? `Verificata · ${m.verificata_da}` : "Da verificare"}
                    </button>
                  </TableCell>
                  <TableCell><Stato m={m} /></TableCell>
                  <TableCell className="whitespace-nowrap text-right">
                    {ed && !bloc && <Button size="sm" variant="ghost" onClick={() => setDlg({ initial: m })} data-testid={`misura-modifica-${m.id}`}>Modifica</Button>}
                    {ed && !bloc && <Button size="sm" variant="ghost" onClick={() => elimina(m)} data-testid={`misura-elimina-${m.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
                    <Button size="sm" variant="ghost" onClick={() => setStorico(m.id)} data-testid={`misura-storico-${m.id}`}><History className="h-3.5 w-3.5" /></Button>
                  </TableCell>
                </TableRow>);
            })}
            {!d.misure.length && <TableRow><TableCell colSpan={7} className="text-sm text-slate-500" data-testid="misure-vuote">Nessuna misura registrata per questa voce.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      {dlg && <MisuraDialog rigaId={rigaId} initial={dlg.initial} onClose={() => setDlg(null)} onSaved={() => { setDlg(null); load(); }} />}
      {storico && <StoricoMisura rid={storico} onClose={() => setStorico(null)} />}
    </CardContent></Card>
  );
};
